/**
 * MasterHomepage — iCRAFTZ Homepage (Mockup v6 approved baseline).
 *
 * The founder-approved painted manuscript IS the homepage. No CSS layer
 * sits on top of the painting. All interactivity is delivered through 22
 * percentage-positioned, fully transparent hotspots so the image stays
 * pixel-identical at every viewport width.
 *
 * Source asset: /illustrations/homepage-master.png (956x944).
 * Will be hot-swapped with a hi-res version once founder provides it.
 */
import React from "react";
import "../styles/MasterHomepageImage.css";

const MASTER = `${process.env.PUBLIC_URL || ""}/illustrations/homepage-master.png`;

/** Percentage-positioned transparent click area. */
function Hotspot({ left, top, width, height, label, testId, onClick, href }) {
  const style = {
    position: "absolute",
    left: `${left}%`,
    top: `${top}%`,
    width: `${width}%`,
    height: `${height}%`,
  };
  const common = {
    className: "master-hotspot",
    "aria-label": label,
    "data-testid": testId,
    style,
  };
  if (href) {
    return (
      <a {...common} href={href}>
        <span className="sr-only">{label}</span>
      </a>
    );
  }
  return (
    <button {...common} type="button" onClick={onClick}>
      <span className="sr-only">{label}</span>
    </button>
  );
}

export default function MasterHomepage({
  onNavigate,
  onLanguageChange,
  activeLang = "en",
}) {
  const go = (path) => () => {
    if (onNavigate) onNavigate(path);
    else window.location.assign(path);
  };

  const scrollTo = (selector) => () => {
    const el = document.querySelector(selector);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 16;
      window.scrollTo({ top: y, behavior: "smooth" });
    } else {
      window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    }
  };

  return (
    <section
      className="master-homepage"
      data-testid="master-homepage"
      aria-label="iCRAFTZ Homepage"
    >
      <div className="master-frame">
        <img
          src={MASTER}
          alt="iCRAFTZ — Where you craft who you become. Timeless wisdom from the world's greatest books."
          className="master-image"
          data-testid="master-homepage-image"
          draggable="false"
        />

        {/* ── Top navigation hotspots ─────────────────────────────── */}
        <Hotspot left={20.0} top={1.2} width={7.0}  height={4.2}
          label="Library" testId="nav-library"
          onClick={scrollTo(".vintage-library-section")} />
        <Hotspot left={28.0} top={1.2} width={9.0}  height={4.2}
          label="Collections" testId="nav-collections"
          onClick={scrollTo(".vintage-library-section")} />
        <Hotspot left={38.0} top={1.2} width={11.0} height={4.2}
          label="My Sanctuary" testId="nav-sanctuary"
          onClick={go("/mycraftz/intro")} />
        <Hotspot left={50.0} top={1.2} width={10.0} height={4.2}
          label="My Journey" testId="nav-journey"
          onClick={go("/mycraftz")} />
        <Hotspot left={61.0} top={1.2} width={8.0}  height={4.2}
          label="Pricing" testId="nav-pricing"
          onClick={go("/pricing")} />

        {/* Language selector — chrome-less native <select> over painted pill */}
        <div
          className="master-language-overlay"
          data-testid="master-language-overlay"
          style={{
            position: "absolute",
            left: "72.0%",
            top: "1.2%",
            width: "12.0%",
            height: "4.2%",
          }}
        >
          <select
            className="master-language-select"
            value={activeLang}
            onChange={(e) => onLanguageChange && onLanguageChange(e.target.value)}
            data-testid="master-language-select"
            aria-label="Language"
          >
            <option value="en">English</option>
            <option value="hi">हिन्दी</option>
            <option value="mr">मराठी</option>
            <option value="gu">ગુજરાતી</option>
            <option value="kn">ಕನ್ನಡ</option>
            <option value="ta">தமிழ்</option>
            <option value="te">తెలుగు</option>
            <option value="ml">മലയാളം</option>
            <option value="bn">বাংলা</option>
          </select>
        </div>

        <Hotspot left={85.0} top={1.2} width={12.0} height={4.2}
          label="Begin Journey (top nav)" testId="nav-cta-begin"
          onClick={go("/signup")} />

        {/* ── Hero CTAs ───────────────────────────────────────────── */}
        <Hotspot left={30.0} top={41.0} width={19.0} height={5.0}
          label="Begin Your Journey" testId="hero-begin-journey"
          onClick={go("/signup")} />
        <Hotspot left={51.0} top={41.0} width={19.0} height={5.0}
          label="Explore Library" testId="hero-explore-library"
          onClick={scrollTo(".vintage-library-section")} />

        {/* ── 5 Stone tablets — Path of Transformation ───────────── */}
        <Hotspot left={3.5}  top={55.5} width={18.0} height={15.5}
          label="Discover (Jigyasa)" testId="tablet-discover"
          onClick={go("/mycraftz/stage/jigyasa")} />
        <Hotspot left={22.9} top={55.5} width={18.0} height={15.5}
          label="Learn (Bodha)" testId="tablet-learn"
          onClick={go("/mycraftz/stage/bodha")} />
        <Hotspot left={42.3} top={55.5} width={18.0} height={15.5}
          label="Commit (Sankalpa)" testId="tablet-commit"
          onClick={go("/mycraftz/stage/sankalpa")} />
        <Hotspot left={61.7} top={55.5} width={18.0} height={15.5}
          label="Practise (Sadhana)" testId="tablet-practise"
          onClick={go("/mycraftz/stage/sadhana")} />
        <Hotspot left={81.1} top={55.5} width={18.0} height={15.5}
          label="Transform (Siddhi)" testId="tablet-transform"
          onClick={go("/mycraftz/stage/siddhi")} />

        {/* ── Popular Collections — "View All" ─────────────────────── */}
        <Hotspot left={88.0} top={72.0} width={9.0} height={3.0}
          label="View All Collections" testId="cta-view-all-collections"
          onClick={scrollTo(".vintage-library-section")} />

        {/* ── 6 Collection cards ─────────────────────────────────── */}
        <Hotspot left={3.5}  top={76.0} width={15.0} height={16.0}
          label="Habits" testId="collection-habits"
          onClick={scrollTo(".vintage-library-section")} />
        <Hotspot left={19.5} top={76.0} width={15.0} height={16.0}
          label="Focus & Deep Work" testId="collection-focus"
          onClick={scrollTo(".vintage-library-section")} />
        <Hotspot left={35.5} top={76.0} width={15.0} height={16.0}
          label="Wealth & Money" testId="collection-wealth"
          onClick={scrollTo(".vintage-library-section")} />
        <Hotspot left={51.5} top={76.0} width={15.0} height={16.0}
          label="Mindset & Growth" testId="collection-mindset"
          onClick={scrollTo(".vintage-library-section")} />
        <Hotspot left={67.5} top={76.0} width={15.0} height={16.0}
          label="Leadership" testId="collection-leadership"
          onClick={scrollTo(".vintage-library-section")} />
        <Hotspot left={83.5} top={76.0} width={15.0} height={16.0}
          label="Purpose & Life" testId="collection-purpose"
          onClick={scrollTo(".vintage-library-section")} />
      </div>
    </section>
  );
}
